import React, { ReactNode } from 'react';
import styles from './donut.css';
import Colors from '../colors';
import { generateColor } from './helpers';

interface TextProps {
  showPercent?: boolean;
  label?: string | number | React.ReactNode;
  percentage: number;
  decimal?: number;
}

interface DonutProps {
  /** Bottom of the range (x) of y */
  current: number;
  /** Top of the range x of (y) */
  max: number;
  /** This will display the percentage in the middle. */
  showPercent?: boolean;
  /** Input your own percent (ie. 24.5 for 24.5%). This will automatically be calculated if left empty. */
  decimal?: number;
  /** Show decimal places after percentage (rounded). */
  percentage?: number;
  /** This is the progress color. */
  color?: Colors;
  /** This is empty space color (remaining). */
  negativeColor?: Colors;
  /** By default the progress starts at the top of the circle. Put in a number for rotation degrees (up to 360) */
  rotate?: number;
  /** Custom label inside donut */
  label?: string | number;
  /** Set to false to prevent color changing when it reaches certain thresholds */
  warn?: boolean;
  /** Set color change thresholds. Example {'40': Colors.Orange,'60': Colors.RadicalRed} will turn orange at 40% and red at 60% */
  warnOptions?: {
    [e: string]: Colors;
  };
  /** Donut thickness */
  donutWidth?: number;
  /** Wrapper Class Name */
  className?: string;
  /** React Node as Donut Contents */
  children?: ReactNode;
}

interface ColorProps {
  warn?: boolean;
  warnOptions: {
    [e: string]: Colors;
  };
  color?: Colors;
  percentage: number;
}

interface StyleProps extends ColorProps {
  rotate: number;
  strokeWidth: number;
}

const circleRadius = 251; // This is generated by the svg as it was created in illustrator

const handleText = ({ showPercent, label, percentage, decimal }: TextProps) => {
  if (showPercent) {
    return `${Number.parseFloat(percentage.toFixed(decimal))}%`;
  }
  if (label) {
    return label;
  }
  return '';
};

const generateDonutStyle = ({
  color,
  rotate,
  percentage,
  warn,
  warnOptions,
  strokeWidth
}: StyleProps) => ({
  stroke: generateColor({ warn, warnOptions, color, percentage }),
  transform: `rotate(${rotate + 270}deg)`,
  strokeDashoffset: circleRadius - circleRadius * ((percentage > 100 ? 100 : percentage) / 100),
  strokeDasharray: circleRadius,
  strokeWidth
});

const generateLabelStyle = (label?: React.ReactNode) => {
  if (label) {
    if (label.toString().length > 30) {
      return { fontSize: '.6em' };
    }
    if (label.toString().length > 20) {
      return { fontSize: '.75em' };
    }
    if (label.toString().length > 10) {
      return { fontSize: '.95em' };
    }
  }
  return {};
};

const Donut = ({
  current,
  max,
  showPercent = false,
  label,
  decimal = 0,
  color = Colors.UnbounceBlue,
  rotate = 0,
  percentage = (current / max) * 100,
  warn = true,
  warnOptions = {
    '75': Colors.Orange,
    '90': Colors.RadicalRed
  },
  negativeColor = Colors.SilverAlto,
  donutWidth: strokeWidth = 13,
  children,
  ...rest
}: DonutProps) => (
  <div className={styles.wrapper} {...rest}>
    {showPercent || label || children ? (
      <div
        className={styles.text}
        data-testid={`section-account-usage-value`}
        style={generateLabelStyle(label)}
      >
        {children ? children : handleText({ showPercent, label, percentage, decimal })}
      </div>
    ) : null}
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" className={styles.donut}>
      <circle
        cx="50"
        cy="50"
        r="40"
        className={styles.underCircle}
        style={{
          stroke: `var(--${negativeColor})`,
          strokeWidth
        }}
      />

      <circle
        cx="50"
        cy="50"
        r="40"
        className={styles.circle}
        data-testid={`section-account-usage-circle`}
        style={generateDonutStyle({ color, rotate, percentage, warn, warnOptions, strokeWidth })}
      />
    </svg>
  </div>
);

export default Donut;
